import React, { useState, useEffect } from 'react';
import { Box, Button, Snackbar, Alert, IconButton, Slide } from '@mui/material';
import { Close as CloseIcon, GetApp as GetAppIcon, IosShare as ShareIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [installSuccess, setInstallSuccess] = useState(false);

  // Detect iOS and standalone mode
  useEffect(() => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                            (window.navigator.standalone === true);
    
    setIsIOS(isIOSDevice);
    setIsInstalled(isStandaloneMode);
  }, []);

  useEffect(() => {
    // Don't show if already installed
    if (isInstalled) {
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // iOS: Show instructions after delay (no beforeinstallprompt event)
    if (isIOS) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return;
    }

    // Android/Desktop: Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 3 seconds
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app was just installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isIOS, isInstalled]);

  const handleInstall = async () => {
    if (isIOS) {
      handleDismiss();
      return;
    }

    if (!deferredPrompt) return;

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setInstallSuccess(true);
        // Track install success (optional analytics)
        console.log('User accepted the install prompt');
        
        // Hide after showing success message
        setTimeout(() => {
          setShowPrompt(false);
          setInstallSuccess(false);
        }, 3000);
      } else {
        console.log('User dismissed the install prompt');
      }
    } catch (error) {
      console.error('Install error:', error);
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't show again for 7 days
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (isInstalled || !showPrompt) {
    return null;
  }

  // Success message
  if (installSuccess) {
    return (
      <Snackbar
        open={installSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
        sx={{ 
          bottom: { xs: 16, sm: 24 },
          zIndex: 9999,
          left: { xs: 16, sm: 'calc(50% + 130px)' },
          right: { xs: 16, sm: 'auto' },
          transform: { sm: 'translateX(-50%)' }
        }}
      >
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{ 
            width: '100%',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: 2
          }}
        >
          Territory CRM installed successfully! 🎉
        </Alert>
      </Snackbar>
    );
  }

  // iOS: Show instructions
  if (isIOS) {
    return (
      <Snackbar
        open={showPrompt}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
        sx={{ 
          bottom: { xs: 16, sm: 24 },
          zIndex: 9999,
          maxWidth: { xs: 'calc(100% - 32px)', sm: '500px' },
          left: { xs: 16, sm: 'calc(50% + 130px)' },
          right: { xs: 16, sm: 'auto' },
          transform: { sm: 'translateX(-50%)' }
        }}
      >
        <Alert
          severity="info"
          icon={<GetAppIcon />}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{ 
            width: '100%',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: 2,
            bgcolor: 'primary.main',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          <Box>
            <Box sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Install Territory CRM
            </Box>
            <Box sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, opacity: 0.95 }}>
              Tap <ShareIcon sx={{ fontSize: '0.875rem', verticalAlign: 'middle', mx: 0.25 }} /> Share, then "Add to Home Screen"
            </Box>
          </Box>
        </Alert>
      </Snackbar>
    );
  }

  // Android/Desktop: Show install button
  if (!deferredPrompt) {
    return null;
  }

  return (
    <Snackbar
      open={showPrompt}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
      sx={{ 
        bottom: { xs: 16, sm: 24 },
        zIndex: 9999,
        maxWidth: { xs: 'calc(100% - 32px)', sm: '500px' },
        left: { xs: 16, sm: 'calc(50% + 130px)' },
        right: { xs: 16, sm: 'auto' },
        transform: { sm: 'translateX(-50%)' }
      }}
    >
      <Alert
        severity="info"
        icon={<GetAppIcon />}
        action={
          <>
            <Button
              variant="contained"
              size="small"
              onClick={handleInstall}
              startIcon={<GetAppIcon />}
              sx={{ 
                mr: 1, 
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)'
                }
              }}
            >
              Install
            </Button>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleDismiss}
              sx={{ color: 'white' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
        sx={{ 
          width: '100%',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          borderRadius: 2,
          bgcolor: 'primary.main',
          color: 'white',
          '& .MuiAlert-icon': {
            color: 'white'
          }
        }}
      >
        <Box sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 500 }}>
          Install Territory CRM for faster access and offline support!
        </Box>
      </Alert>
    </Snackbar>
  );
}

